import { useEffect, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { Upload, X, Lightbulb, Link2, FileText, Rocket, Megaphone } from "lucide-react";
import { trackEvent } from "@/lib/analytics";

const CATEGORIES = ["Policy", "Community", "Technology", "Environment", "Education", "Housing", "Health", "Economic"];

type StarterKey = "policy_ask" | "project_pitch" | "external_signal";
const STARTERS: Record<StarterKey, {
  label: string;
  icon: typeof FileText;
  category: string;
  sourceType: "original" | "external";
  ideaType?: "concept" | "project";
  description: string;
}> = {
  policy_ask: {
    label: "Policy ask",
    icon: Megaphone,
    category: "Policy",
    sourceType: "original",
    ideaType: "concept",
    description:
      "The ask:\n\nWho it helps:\n\nWho needs to act (council, agency, legislature):\n\nEvidence:",
  },
  project_pitch: {
    label: "Project pitch",
    icon: Rocket,
    category: "Community",
    sourceType: "original",
    ideaType: "project",
    description:
      "What we're building:\n\nWho leads it:\n\nWhat we need (people, dollars, partners):\n\nFirst milestone:",
  },
  external_signal: {
    label: "External signal",
    icon: FileText,
    category: "Community",
    sourceType: "external",
    description:
      "Why shareholders should pay attention:",
  },
};

type Prefill = {
  title?: string;
  description?: string;
  category?: string;
  tags?: string[];
};

type EditIdea = {
  id: string;
  title: string;
  description: string | null;
  category: string;
  tags: string[] | null;
  image_url: string | null;
  idea_type?: "concept" | "project" | null;
};

const NewIdeaDialog = ({
  open,
  onOpenChange,
  prefill,
  idea,
  onSaved,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  prefill?: Prefill;
  idea?: EditIdea;
  onSaved?: () => void;
}) => {
  const isEdit = !!idea;
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  useEffect(() => {
    if (open && !isEdit) trackEvent("idea_form_opened");
  }, [open, isEdit]);

  const [title, setTitle] = useState(idea?.title ?? prefill?.title ?? "");
  const [description, setDescription] = useState(idea?.description ?? prefill?.description ?? "");
  const [category, setCategory] = useState(idea?.category ?? prefill?.category ?? "Community");
  const [ideaType, setIdeaType] = useState<"concept" | "project">(
    (idea?.idea_type as "concept" | "project") ?? "concept",
  );
  const [tagsInput, setTagsInput] = useState(
    (idea?.tags ?? prefill?.tags ?? []).join(", "),
  );
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(idea?.image_url ?? null);
  const [existingImageUrl, setExistingImageUrl] = useState<string | null>(idea?.image_url ?? null);
  const [sourceType, setSourceType] = useState<"original" | "external">(
    ((idea as any)?.source_type as "original" | "external") ?? "original",
  );
  const [sourceUrl, setSourceUrl] = useState<string>((idea as any)?.source_url ?? "");
  const [sourceLabel, setSourceLabel] = useState<string>((idea as any)?.source_label ?? "");

  const draftKey = !isEdit && user ? `idea-draft-${user.id}` : null;

  // Re-sync when dialog opens or the target idea changes.
  useEffect(() => {
    if (!open) return;
    // Hydrate from draft when creating a new idea
    let draft: any = null;
    if (!isEdit && draftKey) {
      try {
        const raw = localStorage.getItem(draftKey);
        draft = raw ? JSON.parse(raw) : null;
      } catch {
        draft = null;
      }
    }
    setTitle(idea?.title ?? draft?.title ?? prefill?.title ?? "");
    setDescription(idea?.description ?? draft?.description ?? prefill?.description ?? "");
    setCategory(idea?.category ?? draft?.category ?? prefill?.category ?? "Community");
    setIdeaType((idea?.idea_type as "concept" | "project") ?? draft?.ideaType ?? "concept");
    setTagsInput((idea?.tags ?? prefill?.tags ?? draft?.tags ?? []).join?.(", ") ?? (draft?.tagsInput ?? ""));
    setImageFile(null);
    setImagePreview(idea?.image_url ?? null);
    setExistingImageUrl(idea?.image_url ?? null);
    setSourceType(((idea as any)?.source_type as "original" | "external") ?? draft?.sourceType ?? "original");
    setSourceUrl((idea as any)?.source_url ?? draft?.sourceUrl ?? "");
    setSourceLabel((idea as any)?.source_label ?? draft?.sourceLabel ?? "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, idea?.id]);

  // Autosave draft (debounced via React effect cadence)
  useEffect(() => {
    if (!open || isEdit || !draftKey) return;
    const payload = {
      title, description, category, ideaType,
      tagsInput, sourceType, sourceUrl, sourceLabel,
    };
    const hasContent =
      title.trim() || description.trim() || sourceUrl.trim() || tagsInput.trim();
    try {
      if (hasContent) localStorage.setItem(draftKey, JSON.stringify(payload));
      else localStorage.removeItem(draftKey);
    } catch {
      // ignore quota errors
    }
  }, [open, isEdit, draftKey, title, description, category, ideaType, tagsInput, sourceType, sourceUrl, sourceLabel]);

  const applyStarter = (key: StarterKey) => {
    const s = STARTERS[key];
    setSourceType(s.sourceType);
    setCategory(s.category);
    if (s.ideaType) setIdeaType(s.ideaType);
    if (!description.trim()) setDescription(s.description);
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setImageFile(file);
      setImagePreview(URL.createObjectURL(file));
    }
  };

  const saveIdea = useMutation({
    mutationFn: async () => {
      let imageUrl: string | null = isEdit ? existingImageUrl : null;

      if (imageFile && user) {
        const ext = imageFile.name.split(".").pop();
        const path = `${user.id}/${Date.now()}.${ext}`;
        const { error: uploadError } = await supabase.storage
          .from("idea-images")
          .upload(path, imageFile);
        if (uploadError) throw uploadError;

        const { data: urlData } = supabase.storage
          .from("idea-images")
          .getPublicUrl(path);
        imageUrl = urlData.publicUrl;
      }

      const tags = tagsInput
        .split(",")
        .map((t) => t.trim())
        .filter(Boolean);

      if (isEdit && idea) {
        const { error } = await supabase
          .from("ideas")
          .update({
            title: title.trim(),
            description: description.trim() || null,
            category,
            tags: tags.length > 0 ? tags : null,
            image_url: imageUrl,
            idea_type: ideaType,
            source_type: sourceType,
            source_url: sourceType === "external" ? sourceUrl.trim() : null,
            source_label: sourceType === "external" ? (sourceLabel.trim() || null) : null,
          })
          .eq("id", idea.id);
        if (error) throw error;
      } else {
        const { error } = await supabase.from("ideas").insert({
          title: title.trim(),
          description: description.trim() || null,
          category,
          tags: tags.length > 0 ? tags : null,
          image_url: imageUrl,
          user_id: user!.id,
          idea_type: ideaType,
          source_type: sourceType,
          source_url: sourceType === "external" ? sourceUrl.trim() : null,
          source_label: sourceType === "external" ? (sourceLabel.trim() || null) : null,
        });
        if (error) throw error;
      }
    },
    onSuccess: () => {
      toast({
        title: isEdit ? "Project updated" : "Idea shared!",
        description: isEdit
          ? "Your changes have been saved."
          : "Your idea is now visible to the community.",
      });
      if (!isEdit) {
        trackEvent("idea_submitted", {
          category,
          idea_type: ideaType,
          source_type: sourceType,
          has_image: Boolean(imageFile || existingImageUrl),
        });
      }
      queryClient.invalidateQueries({ queryKey: ["ideas"] });
      if (isEdit && idea) {
        queryClient.invalidateQueries({ queryKey: ["idea-detail", idea.id] });
      }
      if (draftKey) {
        try { localStorage.removeItem(draftKey); } catch { /* noop */ }
      }
      onSaved?.();
      resetForm();
      onOpenChange(false);
    },
    onError: (err: any) => {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    },
  });

  const resetForm = () => {
    if (!isEdit) {
      setTitle("");
      setDescription("");
      setCategory("Community");
      setTagsInput("");
      setImageFile(null);
      setImagePreview(null);
      setExistingImageUrl(null);
      setSourceType("original");
      setSourceUrl("");
      setSourceLabel("");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-card border-border sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="text-foreground">
            {isEdit ? "Edit Project" : "Share an Idea"}
          </DialogTitle>
        </DialogHeader>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            saveIdea.mutate();
          }}
          className="space-y-4"
        >
          {!isEdit && (
            <div>
              <Label className="text-foreground text-xs uppercase tracking-wider text-muted-foreground">
                Start from a template
              </Label>
              <div className="mt-1.5 grid grid-cols-3 gap-2">
                {(Object.keys(STARTERS) as StarterKey[]).map((key) => {
                  const s = STARTERS[key];
                  const Icon = s.icon;
                  return (
                    <button
                      key={key}
                      type="button"
                      onClick={() => applyStarter(key)}
                      className="flex flex-col items-center gap-1 rounded-lg border border-border bg-card px-2 py-2 text-xs text-muted-foreground hover:border-primary/40 hover:text-foreground transition-colors"
                    >
                      <Icon className="h-4 w-4" />
                      {s.label}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {!isEdit && (
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => setSourceType("original")}
                className={`flex items-center justify-center gap-2 rounded-lg border px-3 py-2 text-sm transition-colors ${
                  sourceType === "original"
                    ? "border-primary/40 bg-primary/10 text-primary"
                    : "border-border bg-card text-muted-foreground hover:text-foreground"
                }`}
              >
                <Lightbulb className="h-4 w-4" /> Original idea
              </button>
              <button
                type="button"
                onClick={() => setSourceType("external")}
                className={`flex items-center justify-center gap-2 rounded-lg border px-3 py-2 text-sm transition-colors ${
                  sourceType === "external"
                    ? "border-primary/40 bg-primary/10 text-primary"
                    : "border-border bg-card text-muted-foreground hover:text-foreground"
                }`}
              >
                <Link2 className="h-4 w-4" /> Share a link
              </button>
            </div>
          )}

          {sourceType === "external" && (
            <>
              <div>
                <Label className="text-foreground">Source URL *</Label>
                <Input
                  type="url"
                  value={sourceUrl}
                  onChange={(e) => setSourceUrl(e.target.value)}
                  placeholder="https://example.com/article"
                  required
                  className="mt-1"
                />
              </div>
              <div>
                <Label className="text-foreground">Source name</Label>
                <Input
                  value={sourceLabel}
                  onChange={(e) => setSourceLabel(e.target.value)}
                  placeholder="Publisher or site (optional)"
                  className="mt-1"
                  maxLength={120}
                />
              </div>
            </>
          )}

          <div>
            <Label className="text-foreground">Title *</Label>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="A bold, actionable idea" required className="mt-1" maxLength={200} />
          </div>

          <div>
            <Label className="text-foreground">
              {sourceType === "external" ? "Why this matters" : "Description"}
            </Label>
            <Textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder={
                sourceType === "external"
                  ? "A line or two on why this is worth shareholders' attention..."
                  : "Describe the idea, who it helps, and how it could work..."
              }
              className="mt-1 resize-none"
              rows={sourceType === "external" ? 3 : 4}
              maxLength={2000}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-foreground">Category</Label>
              <Select value={category} onValueChange={setCategory}>
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map((c) => (
                    <SelectItem key={c} value={c}>{c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {sourceType === "original" && (
              <div>
                <Label className="text-foreground">Type</Label>
                <Select value={ideaType} onValueChange={(v) => setIdeaType(v as "concept" | "project")}>
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="concept">Concept</SelectItem>
                    <SelectItem value="project">Project (ongoing)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>

          <div>
            <Label className="text-foreground">Tags</Label>
            <Input value={tagsInput} onChange={(e) => setTagsInput(e.target.value)} placeholder="housing, mobile" className="mt-1" maxLength={200} />
            <p className="text-xs text-muted-foreground mt-0.5">Comma-separated</p>
          </div>

          {/* Image upload — only original ideas */}
          {sourceType === "original" && (
          <div>
            <Label className="text-foreground">Cover Image</Label>
            {imagePreview ? (
              <div className="relative mt-1 rounded-lg overflow-hidden border border-border">
                <img src={imagePreview} alt="Preview" className="w-full h-40 object-cover" />
                <button
                  type="button"
                  onClick={() => {
                    setImageFile(null);
                    setImagePreview(null);
                    setExistingImageUrl(null);
                  }}
                  className="absolute top-2 right-2 bg-background/80 rounded-full p-1"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            ) : (
              <label className="mt-1 flex items-center justify-center gap-2 border-2 border-dashed border-border rounded-lg p-6 cursor-pointer hover:border-primary/40 transition-colors">
                <Upload className="h-5 w-5 text-muted-foreground" />
                <span className="text-sm text-muted-foreground">Upload an image</span>
                <input type="file" className="hidden" accept="image/*" onChange={handleImageChange} />
              </label>
            )}
          </div>
          )}

          <Button
            type="submit"
            className="w-full"
            disabled={
              !title.trim() ||
              saveIdea.isPending ||
              (sourceType === "external" && !sourceUrl.trim())
            }
          >
            {saveIdea.isPending
              ? isEdit ? "Saving..." : "Sharing..."
              : isEdit
                ? "Save Changes"
                : sourceType === "external" ? "Share Link" : "Share Idea"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default NewIdeaDialog;
